import React, { useId } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import DynamicTextareaLabel from "./DynamicTextareaLabel"
import {
    BASE_HEIGHT_PX,
    DEFAULT_MAX_ROWS,
    DEFAULT_ROWS,
    PADDING_HORIZONTAL,
    PADDING_VERTICAL,
    ROW_HEIGHT,
} from "./dynamicTextarea.constants"
import useAutosizingTextarea from "./useAutosizingTextarea"
import { dynamicTextareaPropTypes } from "./dynamicTextarea.propTypes"

const StandardDynamicTextarea = ({
    label,
    value,
    onChange,
    minRows = DEFAULT_ROWS,
    maxRows = DEFAULT_MAX_ROWS,
    disabled,
    readOnly,
    error,
    helperText,
    inputProps,
    onFocus,
    onBlur,
    ...rest
}) => {
    const theme = useTheme()
    const generatedId = useId()
    const labelId = `${generatedId}-label`
    const helperId = `${generatedId}-helper`
    const paddingTotal = PADDING_VERTICAL + PADDING_VERTICAL / 2
    const normalizedMaxRows = Number.isInteger(maxRows) && maxRows >= DEFAULT_ROWS
        ? maxRows
        : DEFAULT_MAX_ROWS
    const maxHeight = normalizedMaxRows * ROW_HEIGHT + paddingTotal
    const valueAsText = `${value ?? ""}`
    const hasErrorMessage = Boolean(error && helperText)
    const hasPlaceholder = Boolean(inputProps?.placeholder) || Boolean(rest.placeholder)
    const isDisabled = disabled
    const nativeInputProps = { ...(inputProps || {}) }
    const nativeTextareaProps = { ...rest }

    delete nativeInputProps.maxLength
    delete nativeTextareaProps.maxLength

    const tokens = theme.palette.input.dynamicTextarea
    const {
        textareaRef,
        height,
        focused,
        showScroll,
        handleChange,
        handleFocus,
        handleBlur,
    } = useAutosizingTextarea({
        value: valueAsText,
        onChange,
        onFocus,
        onBlur,
        minimumHeight: BASE_HEIGHT_PX,
        maximumHeight: maxHeight,
        scrollHeightOffset: 2,
        overflowThreshold: 2,
    })
    const shrink = focused || Boolean(valueAsText) || hasPlaceholder
    const baseBg = isDisabled ? tokens.background.disabled : tokens.background.default
    const baseBorder = error ? tokens.border.error : tokens.border.default
    const baseColor = isDisabled ? tokens.text.disabled : tokens.text.default
    const hoverBg = tokens.background.hover
    const hoverBorder = error ? tokens.border.error : tokens.border.hover

    return (
        <Box
            sx={{
                width: "100%",
                position: "relative",
                "& .dynamic-textarea": {
                    width: "100%",
                    resize: "none",
                    padding: `${PADDING_VERTICAL}px ${PADDING_HORIZONTAL}px ${PADDING_VERTICAL / 2}px ${PADDING_HORIZONTAL}px`,
                    borderRadius: 0,
                    border: `1px solid ${baseBorder}`,
                    backgroundColor: baseBg,
                    color: baseColor,
                    fontSize: "14px",
                    lineHeight: "20px",
                    fontFamily: theme.typography.fontFamily,
                    boxSizing: "border-box",
                    outline: "none",
                    overflowY: showScroll ? "auto" : "hidden",
                    overflowX: "hidden",
                    opacity: isDisabled ? 0.7 : 1,
                    transition: "height 0.2s ease, min-height 0.2s ease, max-height 0.2s ease, border-color 0.2s ease, background-color 0.2s ease",
                    display: "block",
                },
                "& .dynamic-textarea:not(:disabled):hover": {
                    backgroundColor: hoverBg,
                    borderColor: hoverBorder,
                },
                "& .dynamic-textarea:focus-visible": {
                    borderColor: error ? tokens.border.error : tokens.border.focus,
                },
                "& .dynamic-textarea:disabled:hover": {
                    backgroundColor: hoverBg,
                    color: baseColor,
                },
            }}
        >
            {label && (
                <DynamicTextareaLabel label={label} labelId={labelId} shrink={shrink} />
            )}
            <textarea
                {...nativeInputProps}
                {...nativeTextareaProps}
                ref={textareaRef}
                rows={minRows}
                value={valueAsText}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                disabled={isDisabled}
                readOnly={readOnly}
                aria-labelledby={label ? labelId : undefined}
                aria-describedby={hasErrorMessage ? helperId : undefined}
                aria-invalid={error || undefined}
                style={{
                    minHeight: BASE_HEIGHT_PX,
                    maxHeight,
                    height,
                    transition: "height 0.2s ease, min-height 0.2s ease, max-height 0.2s ease",
                }}
                className="dynamic-textarea"
            />
            {hasErrorMessage && (
                <Typography id={helperId} variant="caption" sx={{ color: tokens.border.error, mt: 0.5, display: "block" }}>
                    {helperText}
                </Typography>
            )}
        </Box>
    )
}

StandardDynamicTextarea.propTypes = dynamicTextareaPropTypes

export default StandardDynamicTextarea
